import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import type { ColumnFiltersState, PaginationState } from '@tanstack/react-table';
import { unparse } from 'papaparse';
import { toast } from 'sonner';
import { adminLocales, useApiAdminlocalesIdDelete } from '@r/api/generated/client.ts';
import type {
  UserWorkspaceMeReadModel,
  LocaleLocaleAdminReadModel,
  AdminLocalesParams
} from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { DataTableAdvancedToolbar } from '@r/components/data-table/advanced/data-table-advanced-toolbar.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import {
  useAdminTranslationColumns,
  useFilterFields
} from '@admin/components/translations/admin-translation-columns.tsx';
import { downloadCSV } from '@r/utils/utils.ts';
import { useUser } from '@r/api/auth.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@r/components/ui/dialog.tsx';
import { AdminLocaleToEditForm } from '@admin/components/forms/locale/admin-locale-to-edit-form.tsx';
import { AdminLocaleForm } from '@admin/components/forms/locale/admin-locale-form.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { getAdminTablePersistenceKey } from '@r/utils/data-table-persistence.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type UserWorkspace = UserWorkspaceMeReadModel;
type LocaleCollection = LocaleLocaleAdminReadModel;

export function AdminTranslations() {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const user = useUser();
  const userWorkspaces: UserWorkspace[] = user.data?.visibleWorkspaces ?? [];
  const activeWorkspace = getActiveWorkspaceUniversal(userWorkspaces);
  const isUserSuperAdmin = activeWorkspace?.roles?.includes('ROLE_FIDDY_ADMIN') || false;

  const [isExporting, setIsExporting] = useState<boolean>(false);

  const [addModalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [translationToEdit, setTranslationToEdit] = useState<LocaleCollection | null>(null);

  const adminTranslationsTablePersistenceKey = useMemo(
    // Cle de persistance isolee par utilisateur pour conserver ses preferences.
    () => getAdminTablePersistenceKey('admin-translations', user.data?.id),
    [user.data?.id]
  );

  async function translationsListFiltersQuery(
    filters: {
      search?: string;
      pageIndex: number;
      pageSize: number;
    },
    page: number | null = null,
    limit: number | null = null
  ) {
    const { search, pageIndex: defaultPageIndex, pageSize: defaultPageSize } = filters;

    try {
      const params: AdminLocalesParams = {
        page: page ?? defaultPageIndex + 1,
        perPage: limit ?? defaultPageSize,
        ...(search && { search })
      };
      return await adminLocales(params);
    } catch (error) {
      if (isOrvalErrorFromPath(error, 'get', '/admin/locales')) {
        showOrvalErrorToast(error);
      }
      throw error;
    }
  }

  const deleteLocaleMutation = useApiAdminlocalesIdDelete({
    mutation: {
      onSuccess: () => {
        toast.success(t('toast.success.default.translation.deleted'));
        translationsListQuery.refetch();
      },
      onError: (error) => {
        if (isOrvalErrorFromPath(error, 'delete', '/admin/locales')) {
          showOrvalErrorToast(error);
        } else {
          toast.error(t('toast.error.generic'));
        }
      }
    }
  });

  async function deleteLocale(localeId: number) {
    if (!isUserSuperAdmin) {
      toast.error(t('toast.error.not.authorized'));
      throw new Error('Not authorized');
    }
    deleteLocaleMutation.mutate({ id: localeId.toString() });
  }

  const onEditLocale = (locale: LocaleCollection) => {
    setTranslationToEdit(locale);
    setEditModalOpen(true);
  };

  const onDeleteLocale = (locale: LocaleCollection) => {
    toast(`${t('admin.translations.delete.confirm')} "${locale.label}" ?`, {
      action: {
        label: t('label.confirm'),
        onClick: () => deleteLocale(locale.id ?? 0)
      },
      cancel: {
        label: t('label.cancel'),
        onClick: () => {}
      }
    });
  };

  function getTranslationDataForExport(locale: LocaleCollection) {
    return [locale.label, locale.content];
  }

  // Helper function to get current filters from table state (like users.tsx)
  // This function reads from closure - variables will be defined after table creation
  function translationsListFilters() {
    const search = columnFilters.find((filter) => filter.id === 'search');
    return {
      search: search?.value as string,
      pageIndex,
      pageSize
    };
  }

  const translationsListQuery = useQuery({
    queryKey: ['translations_list'],
    queryFn: async () => {
      return await translationsListFiltersQuery(translationsListFilters());
    }
  });

  const filterFields = useFilterFields();

  const { table } = useDataTable({
    data: translationsListQuery.data?.data ?? [],
    columns: useAdminTranslationColumns(isUserSuperAdmin, onEditLocale, onDeleteLocale),
    filterFields,
    pageCount: translationsListQuery.data?.pagination?.last ?? 1,
    enableAdvancedFilter: true,
    persistence: {
      // Persiste pagination, tri et filtres en localStorage.
      key: adminTranslationsTablePersistenceKey
    }
  });

  const { pageIndex, pageSize }: PaginationState = table.getState().pagination as PaginationState;
  const sorting = table.getState().sorting;
  const columnFilters: ColumnFiltersState = table.getState().columnFilters;
  const prevValuesRef = useRef({ pageIndex, pageSize, columnFilters, sorting });

  async function exportData() {
    setIsExporting(true);

    let allData: LocaleCollection[] = [];
    const limit = 500;

    const totalPageCount = Math.ceil(
      (translationsListQuery.data?.pagination?.totalItems ?? 1) / limit
    );

    const promises = Array.from({ length: totalPageCount }, (_, i) =>
      translationsListFiltersQuery(translationsListFilters(), i + 1, limit)
    );

    const pagesData = await Promise.all(promises);
    allData = pagesData.flatMap((pageData) => pageData.data);

    const csv = unparse({
      fields: [t('label.translation.label'), t('label.translation.content')],
      data: allData.map((translation) => getTranslationDataForExport(translation))
    });

    downloadCSV(csv, `translations-export-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`);
    setIsExporting(false);
  }

  useEffect(() => {
    const prevValues = prevValuesRef.current;
    if (
      pageIndex != prevValues.pageIndex ||
      pageSize != prevValues.pageSize ||
      JSON.stringify(columnFilters) !== JSON.stringify(prevValues.columnFilters) ||
      JSON.stringify(sorting) != JSON.stringify(prevValues.sorting)
    ) {
      translationsListQuery.refetch();
    }

    prevValuesRef.current = { pageIndex, pageSize, columnFilters, sorting };
  }, [pageIndex, pageSize, columnFilters, sorting]);

  return (
    <Fragment>
      <div className="space-y-2.5">
        <h2 className="text-4xl font-bold">{t('admin.translations.title')}</h2>
        <p className="font-light text-muted-foreground">{t('admin.translations.description')}</p>
      </div>

      {translationsListQuery.isLoading && (
        <DataTableSkeleton
          columnCount={10}
          cellWidths={['10rem', '40rem', '12rem', '12rem', '8rem']}
        />
      )}

      {translationsListQuery.isSuccess && (
        <Fragment>
          <DataTableAdvancedToolbar
            table={table}
            filterFields={filterFields}
            onExport={exportData}
            isExporting={isExporting}
            canAdd={isUserSuperAdmin}
            onAdd={() => {
              setTranslationToEdit(null);
              setEditModalOpen(false);
              setModalOpen(true);
            }}
          />

          <DataTable
            table={table}
            totalItems={translationsListQuery.data?.pagination?.totalItems}
          />
        </Fragment>
      )}

      <Dialog
        open={editModalOpen || addModalOpen}
        onOpenChange={addModalOpen ? setModalOpen : setEditModalOpen}
      >
        <DialogContent className="p-10 !rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {t(`admin.translations.${translationToEdit ? 'edit' : 'new'}.title`)}
            </DialogTitle>
            <DialogDescription>{t('translation.reminder.title')}</DialogDescription>
            <DialogDescription className="mt-2">
              <span className="flex flex-col gap-y-1 pl-5 space-y-1 text-start">
                <span>• {t('translation.reminder.code')}</span>
                <span>• {t('translation.reminder.value')}</span>
              </span>
            </DialogDescription>
          </DialogHeader>

          {translationsListQuery.isLoading && (
            <DataTableSkeleton columnCount={8} cellWidths={['10rem', '40rem', '12rem', '8rem']} />
          )}

          {translationToEdit ? (
            <AdminLocaleToEditForm
              locale={translationToEdit}
              isSuperAdmin={isUserSuperAdmin}
              onSuccess={() => {
                setEditModalOpen(false);
                setTranslationToEdit(null);
                translationsListQuery.refetch();
              }}
            />
          ) : (
            <AdminLocaleForm
              isUserSuperAdmin={isUserSuperAdmin}
              onSuccess={() => {
                setModalOpen(false);
                setTranslationToEdit(null);
                translationsListQuery.refetch();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Fragment>
  );
}
